import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import {
  ChildrenSelectorComponent,
  Student,
} from '../../shared/children-selector/children-selector.component';
import { PageTitleComponent } from '../../shared/page-title/page-title.component';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { DocumentService, Document, DocumentResponse } from '../../services/document.service';
import { TranslatePipe } from '@ngx-translate/core';
import { LoaderComponent } from '../../shared/loader/loader.component';
import { DownloadFilesService } from '../../services/download-files.service';
import { NotificationService } from '../../services/notification.service';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface UserData {
  iduser: number;
  role: number;
  idTeacher: number;
  orgid: number;
  testmobile: number;
  error: string;
  listStudent: Student[];
}

@Component({
  selector: 'app-documents',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ChildrenSelectorComponent,
    PageTitleComponent,
    TranslatePipe,
    LoaderComponent,
  ],
  templateUrl: './documents.component.html',
  styleUrl: './documents.component.css',
})
export class DocumentsComponent implements OnInit, OnDestroy {
  students: Student[] = [];
  selectedStudent: Student | null = null;
  documents: Document[] = [];
  userData: UserData | null = null;
  loading = false;
  selectedDocument: Document | null = null;
  error: string | null = null; // ✅ essentiel
  currentPage = 1;
  itemsPerPage = 12;
  totalPages = 1;
  
  private destroy$ = new Subject<void>();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private documentService: DocumentService,
    private downloadService: DownloadFilesService,
    private notificationService: NotificationService
  ) {}

ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      const userDataStr = localStorage.getItem('userData');
      if (userDataStr) {
        this.userData = JSON.parse(userDataStr);
      }

      const studentList = localStorage.getItem('studentList');
      if (studentList) {
        this.students = JSON.parse(studentList);
        if (this.students.length > 0) {
          this.selectedStudent = this.students[0];
          this.loadStudentDocuments(this.students[0]);
        }
      }
    }
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onStudentSelected(student: Student) {
    this.selectedStudent = student;
    this.currentPage = 1;
    this.loadStudentDocuments(student);
  }

  onStudentsLoaded(students: Student[]) {
    this.students = students;
    if (!this.selectedStudent && students.length > 0) {
      this.selectedStudent = students[0];
      this.loadStudentDocuments(students[0]);
    }
  }

loadStudentDocuments(student: Student): void {
    if (!this.userData) return;

    this.loading = true;
    this.error = null;
    // Fetch page 1 first to discover totalPage, then fetch remaining pages
    this.documentService
      .getStudentDocuments(
        student.nameOrg,
        this.userData.role,
        student.gradeId,
        student.batchId,
        student.idStudent,
        this.userData.iduser,
        1,
        10
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (firstPageDocs: Document[]) => {
          // Determine totalPage from the first document (API attaches it to each doc)
          const serverTotalPages = firstPageDocs.length > 0 && firstPageDocs[0].totalPage
            ? firstPageDocs[0].totalPage
            : 1;
          if (serverTotalPages <= 1) {
            // Only 1 page, we already have all documents
            this.applyDocuments(firstPageDocs);
          } else {
            // Fetch remaining pages in parallel
            const remainingRequests = [];
            for (let page = 2; page <= serverTotalPages; page++) {
              remainingRequests.push(
                this.documentService.getStudentDocuments(
                  student.nameOrg,
                  this.userData!.role,
                  student.gradeId,
                  student.batchId,
                  student.idStudent,
                  this.userData!.iduser,
                  page,
                  10
                )
              );
            }

            forkJoin(remainingRequests)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (otherPages: Document[][]) => {
                  const allDocs = [...firstPageDocs];
                  for (const pageDocs of otherPages) {
                    allDocs.push(...pageDocs);
                  }
                  this.applyDocuments(allDocs);
                },
                error: (error) => {
                  console.error('❌ Erreur lors du chargement des pages suivantes:', error);
                  this.applyDocuments(firstPageDocs);
                },
              });
          }
        },
        error: (error) => {
          console.error('❌ Erreur lors du chargement des documents:', error);
          this.error = 'Impossible de charger les documents.';
          this.documents = [];
          this.totalPages = 1;
          this.loading = false;
        },
      });
  }

  private applyDocuments(documents: Document[]): void {
    this.documents = documents || [];
    this.totalPages = this.documents.length > 0
      ? Math.ceil(this.documents.length / this.itemsPerPage)
      : 1;

    console.log('Pagination Config:', {
      totalItems: this.documents.length,
      itemsPerPage: this.itemsPerPage,
      totalPages: this.totalPages,
    });

    this.currentPage = 1;
    this.loading = false;
  }

  get paginatedDocuments(): Document[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.documents.slice(startIndex, endIndex);
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    if (isPlatformBrowser(this.platformId)) {
      this.windowScrollToTopSmooth();
    }
  }

  private windowScrollToTopSmooth() {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  openDocumentModal(document: Document) {
    this.selectedDocument = document;
  }

  closeDocumentModal() {
    this.selectedDocument = null;
  }

  /*   getFileName(filePath: string): string {
    return filePath.split('/').pop() || filePath;
  }

  downloadFile(filePath: string) {
    const baseUrl = 'https://plateforme.educanet.pro/';
    const downloadUrl = baseUrl + filePath.trim();
    window.open(downloadUrl, '_blank');
  } */

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  downloadFile(filePath: string) {
    const fileName = this.getFileName(filePath);

    // Update notification status when document is downloaded
    if (this.selectedDocument && this.selectedStudent) {
      const idDoc = this.selectedDocument.id;
      const idStudent = this.selectedStudent.idStudent;

      this.notificationService.updateNotificationStatus({
        idType: idDoc,
        idStudent: idStudent,
        nameType: 'Document'
      })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            console.log(`Document notification ${idDoc} marked as seen`);
            // Update the document's vu status locally
            this.selectedDocument = { ...this.selectedDocument!, vu: '1' };
            // Update the documents array to reflect the change
            this.documents = this.documents.map(doc => 
              doc.id === idDoc ? { ...doc, vu: '1' } : doc
            );
            // Update notification counts
            this.updateNotificationCounts();
          },
          error: (err) => console.error('Error updating document notification:', err)
        });
    }

    if (isPlatformBrowser(this.platformId)) {
      this.openDownloadWindow(filePath, fileName);
    }
  }

  private openDownloadWindow(filePath: string, fileName: string) {
    console.log('file', filePath);
    const trimmed = (filePath || '').trim();
    const baseUrl = 'https://plateforme.educanet.pro/';

    // Build downloadUrl handling several cases:
    // 1. Absolute URL with protocol (may be missing slash after domain)
    // 2. Host-only string like 'gateway.educanet.pro/...' (missing protocol)
    // 3. Relative path (should be prefixed with platform base)
    let downloadUrl = trimmed;

    if (/^https?:\/\//i.test(trimmed)) {
      // Absolute with protocol: ensure there's a slash after the domain
      const absMatch = trimmed.match(/^(https?:\/\/[^\/]+)\/?(.*)$/i);
      if (absMatch) {
        const host = absMatch[1];
        const rest = absMatch[2] || '';
        downloadUrl = rest ? `${host}/${rest}` : `${host}/`;
      }
    } else {
      // Not starting with http(s):// -> check if it starts with a host like 'gateway.educanet.pro'
      const hostMatch = trimmed.match(/^([a-z0-9.-]+\.educanet\.pro)\/?(.*)$/i);
      if (hostMatch) {
        const host = hostMatch[1];
        const rest = hostMatch[2] || '';
        downloadUrl = rest ? `https://${host}/${rest}` : `https://${host}/`;
      } else {
        // Treat as relative path and prefix the default base URL
        const normalized = trimmed.replace(/^\/+/, '');
        downloadUrl = baseUrl + normalized;
      }
    }

    // Encode the URL to ensure it's safe for window.open
    try {
      // If the URL contains an educanet.pro host but missing a slash after the domain
      // (e.g. https://gateway.educanet.proarchivefile/...), insert the missing slash.
      downloadUrl = downloadUrl.replace(/(https?:\/\/[^\/]*\.educanet\.pro)(?!\/)/i, '$1/');

      // Force host replacement: gateway.educanet.pro -> plateforme.educanet.pro
      downloadUrl = downloadUrl.replace(/gateway\.educanet\.pro/gi, 'plateforme.educanet.pro');

      downloadUrl = encodeURI(downloadUrl);
    } catch (e) {
      // fallback to raw string if encodeURI fails for some reason
    }

    if (typeof window !== 'undefined') {
      console.log('🔗 Opening download URL:', downloadUrl);
      window.open(downloadUrl, '_blank');
    }
  }

  private updateNotificationCounts(): void {
    // Get userData from localStorage to access iduser
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    if (userData.iduser) {
      this.notificationService.getNotificationCounts(userData.iduser)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          error: (err) => console.error('Error updating notification counts:', err)
        });
    }
  }

  getFileName(filePath: string): string {
    return filePath?.split('/').pop() || 'document.pdf';
  }
}
